import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, Pencil, Plus, Trash2 } from "lucide-react";

import {
  api,
  ApiError,
  type CalendarExceptionDay,
  type CalendarResponse,
} from "@/api/client";
import Panel from "@/components/common/Panel";
import OtpInput from "@/components/OtpInput";
import { Button } from "@/components/ui/button";
import { ErrorState, LoadingState } from "@/components/common/states";
import { cn } from "@/lib/utils";

/**
 * V20 — calendrier de marché (colonne de travail des Réglages) : bande live
 * (ouvert/fermé + prochaine fermeture + instant du flatten — ou « flatten OFF »
 * explicite, §25), horaire hebdo éditable, jours d'exception. Mutations gardées
 * par OTP (même code PARAM_APPLY que l'apply de paramètres) ; jamais un ordre IG.
 * Purge 2026-07-16 : écho UTC, écho Paris, colonne source, champ note visible et
 * pédagogie DST retirés (bruit sans décision) — la note existante survit au
 * round-trip d'édition.
 */
export default function MarketCalendarPanel() {
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [editing, setEditing] = useState<ExceptionDraft | null>(null);
  const [editingWeekly, setEditingWeekly] = useState(false);

  const reload = useCallback(() => {
    api
      .calendar()
      .then((c) => {
        setData(c);
        setError(null);
        setLoading(false);
      })
      .catch(() => {
        setError("Impossible de charger /api/calendar");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const today = new Date().toISOString().slice(0, 10);
  const { upcoming, past } = useMemo(() => {
    const ex = data?.exceptions ?? [];
    return {
      upcoming: ex.filter((e) => e.date >= today),
      past: ex.filter((e) => e.date < today),
    };
  }, [data, today]);

  return (
    <Panel
      title="Calendrier de marché"
      icon={<CalendarClock className="text-muted-foreground size-4" aria-hidden />}
      aria-label="Calendrier de marché"
      aside={<span>pilote le flatten de fermeture · OTP</span>}
      testId="market-calendar-panel"
    >
      {loading && <LoadingState lines={3} />}
      {error && <ErrorState title={error} testId="calendar-error" />}
      {data && (
        <div className="flex flex-col gap-3">
          <NowStrip derived={data.derived} />

          {/* weekly schedule */}
          <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="text-dim font-semibold tracking-wider uppercase">Hebdo</span>
            {data.weekly ? (
              <span data-testid="calendar-weekly">
                ouvre <b className="text-foreground font-mono">{data.weekly.weekOpenDow.slice(0, 3)}{" "}
                {data.weekly.weekOpenTime}</b> · ferme{" "}
                <b className="text-foreground font-mono">{data.weekly.weekCloseDow.slice(0, 3)}{" "}
                {data.weekly.weekCloseTime}</b>{" "}
                <span className="text-dim">({data.weekly.anchorTz})</span>
              </span>
            ) : (
              <span data-testid="calendar-weekly">? (ligne hebdo absente)</span>
            )}
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              onClick={() => setEditingWeekly((v) => !v)}
              data-testid="calendar-weekly-edit"
            >
              {editingWeekly ? "fermer" : "éditer"}
            </button>
          </div>
          {editingWeekly && data.weekly && (
            <WeeklyForm
              weekly={data.weekly}
              onDone={() => {
                setEditingWeekly(false);
                reload();
              }}
            />
          )}

          {/* exceptions */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3 font-medium">date</th>
                  <th className="py-1 pr-3 font-medium">statut</th>
                  <th className="py-1 pr-3 font-medium">heures (locales)</th>
                  <th className="py-1 pr-3 font-medium">libellé</th>
                  <th className="py-1 pr-3 font-medium" aria-label="actions" />
                </tr>
              </thead>
              <tbody data-testid="calendar-exceptions">
                {(showPast ? [...past, ...upcoming] : upcoming).map((e) => (
                  <ExceptionRow
                    key={e.date}
                    e={e}
                    past={e.date < today}
                    onEdit={() => setEditing(draftOf(e))}
                    onDeleted={reload}
                  />
                ))}
                {upcoming.length === 0 && !showPast && (
                  <tr>
                    <td colSpan={5} className="text-muted-foreground py-2">
                      Aucun jour d'exception à venir.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditing(emptyDraft())}
              data-testid="calendar-add-exception"
            >
              <Plus className="size-4" aria-hidden /> Ajouter une exception
            </Button>
            {past.length > 0 && (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
                onClick={() => setShowPast((v) => !v)}
                data-testid="calendar-show-past"
              >
                {showPast ? "Masquer le passé" : `Afficher le passé (${past.length})`}
              </button>
            )}
          </div>

          {editing && (
            <ExceptionForm
              draft={editing}
              anchorTz={data.weekly?.anchorTz ?? "?"}
              lead={data.derived.flattenLeadMinutes}
              flatOn={data.derived.flatWhenClosed}
              onChange={setEditing}
              onDone={() => {
                setEditing(null);
                reload();
              }}
              onCancel={() => setEditing(null)}
            />
          )}
        </div>
      )}
    </Panel>
  );
}

/** Bande live : ouvert/fermé, prochaine fermeture (Paris), instant du flatten ou OFF explicite (§25). */
function NowStrip({ derived }: { derived: CalendarResponse["derived"] }) {
  return (
    <div
      className="border-border bg-surface-2/50 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-xs"
      data-testid="calendar-now"
    >
      {derived.open ? (
        <span className="text-buy inline-flex items-center gap-1.5 font-bold">
          <span className="bg-buy size-2 rounded-full shadow-[0_0_8px_var(--color-buy)]" aria-hidden />
          MARCHÉ OUVERT
        </span>
      ) : (
        <span className="text-muted-foreground inline-flex items-center gap-1.5 font-bold">
          <span className="bg-idle size-2 rounded-full" aria-hidden />
          MARCHÉ FERMÉ
          {derived.closedReason && (
            <span className="text-dim font-normal" data-testid="calendar-closed-reason">
              — {derived.closedReason}
            </span>
          )}
        </span>
      )}
      <span className="bg-border-strong h-4 w-px" aria-hidden />
      <span className="text-muted-foreground">
        Prochaine fermeture :{" "}
        <b className="text-foreground font-mono tabular-nums" data-testid="calendar-next-close">
          {fmtParis(derived.nextCloseUtc)}
        </b>
      </span>
      {derived.flatWhenClosed ? (
        <span
          className="text-accent-neon border-accent-neon/45 bg-accent-soft inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono font-semibold tabular-nums"
          data-testid="calendar-flatten-at"
        >
          ⏱ flatten {fmtParis(derived.flattenAtUtc)} (T−{derived.flattenLeadMinutes})
        </span>
      ) : (
        <span
          className="text-warning border-warning/45 bg-warning-soft inline-flex items-center rounded-md border px-2 py-0.5 font-semibold"
          data-testid="calendar-flatten-off"
          title="flatWhenClosed=false — le flatten de pré-fermeture ne tirera PAS (éditable dans Paramètres)"
        >
          flatten OFF (param)
        </span>
      )}
      {!derived.open && derived.nextOpenUtc && (
        <span className="text-dim">réouvre {fmtParis(derived.nextOpenUtc)}</span>
      )}
    </div>
  );
}

function ExceptionRow({
  e,
  past,
  onEdit,
  onDeleted,
}: {
  e: CalendarExceptionDay;
  past: boolean;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [otp, setOtp] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doDelete = async () => {
    setPending(true);
    setErr(null);
    try {
      await api.deleteCalendarException(e.date, otp);
      setConfirming(false);
      onDeleted();
    } catch (ex) {
      setErr(ex instanceof ApiError && ex.status === 401 ? "OTP invalide." : "Suppression échouée.");
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <tr className={cn("border-border/40 border-t", past && "opacity-45")} data-testid={`calendar-row-${e.date}`}>
        <td className="py-1 pr-3 font-mono whitespace-nowrap">{e.date}</td>
        <td className="py-1 pr-3">
          <TypeBadge type={e.type} />
        </td>
        <td className="py-1 pr-3 font-mono tabular-nums whitespace-nowrap">{hoursOf(e)}</td>
        <td className="max-w-64 truncate py-1 pr-3" title={e.label}>
          {e.label}
        </td>
        <td className="py-1 pr-3 whitespace-nowrap">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground rounded p-1"
            onClick={onEdit}
            aria-label={`Éditer ${e.date}`}
            data-testid={`calendar-edit-${e.date}`}
          >
            <Pencil className="size-3.5" aria-hidden />
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-loss rounded p-1"
            onClick={() => {
              setConfirming((v) => !v);
              setOtp("");
              setErr(null);
            }}
            aria-label={`Supprimer ${e.date}`}
            data-testid={`calendar-delete-${e.date}`}
          >
            <Trash2 className="size-3.5" aria-hidden />
          </button>
        </td>
      </tr>
      {confirming && (
        <tr className="border-border/40 border-t">
          <td colSpan={5} className="py-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">
                Supprimer « {e.label} » — code reçu par e-mail :
              </span>
              <OtpButton />
              <OtpInput value={otp} onChange={setOtp} />
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={pending || otp.length !== 6}
                onClick={doDelete}
                data-testid={`calendar-delete-confirm-${e.date}`}
              >
                {pending ? "Suppression…" : "Supprimer"}
              </Button>
              {err && (
                <span className="text-loss" role="alert">
                  {err}
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── forms ─────────────────────────────────────────────────────────────── */

interface ExceptionDraft {
  date: string;
  type: CalendarExceptionDay["type"];
  openLocal: string;
  closeLocal: string;
  reopenLocal: string;
  label: string;
  note: string;
  otp: string;
}

function emptyDraft(): ExceptionDraft {
  return { date: "", type: "CLOSED", openLocal: "", closeLocal: "", reopenLocal: "", label: "", note: "", otp: "" };
}

function draftOf(e: CalendarExceptionDay): ExceptionDraft {
  return {
    date: e.date,
    type: e.type,
    openLocal: e.openLocal ?? "",
    closeLocal: e.closeLocal ?? "",
    reopenLocal: e.reopenLocal ?? "",
    label: e.label,
    note: e.note ?? "",
    otp: "",
  };
}

const FIELD = "field w-36 tabular-nums";

function ExceptionForm({
  draft,
  anchorTz,
  lead,
  flatOn,
  onChange,
  onDone,
  onCancel,
}: {
  draft: ExceptionDraft;
  anchorTz: string;
  lead: number;
  flatOn: boolean;
  onChange: (d: ExceptionDraft) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (patch: Partial<ExceptionDraft>) => onChange({ ...draft, ...patch });

  const shapeOk =
    draft.date !== "" &&
    draft.label.trim() !== "" &&
    (draft.type !== "EARLY_CLOSE" || draft.closeLocal !== "") &&
    (draft.type !== "MODIFIED" || (draft.openLocal !== "" && draft.closeLocal !== ""));

  const submit = async () => {
    setPending(true);
    setErr(null);
    try {
      await api.putCalendarException(draft.date, {
        type: draft.type,
        openLocal: draft.openLocal || null,
        closeLocal: draft.closeLocal || null,
        reopenLocal: draft.reopenLocal || null,
        label: draft.label.trim(),
        note: draft.note.trim() || null,
        otp: draft.otp,
      });
      onDone();
    } catch (ex) {
      setErr(
        ex instanceof ApiError && ex.status === 401
          ? "OTP invalide."
          : ex instanceof ApiError && ex.status === 400
            ? "Champs invalides (vérifie date/heures)."
            : "Enregistrement échoué.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="border-border bg-surface-2/50 flex flex-col gap-2 rounded-md border p-3 text-xs"
      data-testid="calendar-exception-form"
    >
      <span className="text-foreground text-sm font-semibold">
        {draft.label ? `Éditer l'exception — ${draft.date}` : "Ajouter une exception"}
      </span>
      <span className="text-muted-foreground">
        Heures locales à <b>{anchorTz}</b>.
      </span>
      <div className="flex flex-wrap gap-3">
        <L label="Date">
          <input type="date" className={FIELD} value={draft.date} onChange={(e) => set({ date: e.target.value })} data-testid="calendar-form-date" />
        </L>
        <L label="Statut">
          <select className={FIELD} value={draft.type} onChange={(e) => set({ type: e.target.value as ExceptionDraft["type"] })} data-testid="calendar-form-type">
            <option value="CLOSED">Fermé toute la journée</option>
            <option value="EARLY_CLOSE">Fermeture anticipée</option>
            <option value="MODIFIED">Fenêtre personnalisée</option>
          </select>
        </L>
        {draft.type === "MODIFIED" && (
          <L label="Ouvre à">
            <input type="time" className={FIELD} value={draft.openLocal} onChange={(e) => set({ openLocal: e.target.value })} data-testid="calendar-form-open" />
          </L>
        )}
        {draft.type !== "CLOSED" && (
          <L label="Ferme à">
            <input type="time" className={FIELD} value={draft.closeLocal} onChange={(e) => set({ closeLocal: e.target.value })} data-testid="calendar-form-close" />
          </L>
        )}
        {draft.type === "EARLY_CLOSE" && (
          <L label="Réouvre (optionnel)">
            <input type="time" className={FIELD} value={draft.reopenLocal} onChange={(e) => set({ reopenLocal: e.target.value })} data-testid="calendar-form-reopen" />
          </L>
        )}
        <L label="Libellé">
          <input type="text" className="field w-72" value={draft.label} onChange={(e) => set({ label: e.target.value })} placeholder="ex. Vendredi saint — marchés UE fermés" data-testid="calendar-form-label" />
        </L>
      </div>
      {flatOn && draft.type !== "CLOSED" && draft.closeLocal && (
        <p className="text-accent-neon text-xs" data-testid="calendar-form-preview">
          Aperçu : flatten du livre {lead} min avant {draft.closeLocal} ({anchorTz}) le {draft.date || "…"}.
        </p>
      )}
      {flatOn && draft.type === "CLOSED" && draft.date && (
        <p className="text-accent-neon text-xs" data-testid="calendar-form-preview">
          Aperçu : flatten du livre {lead} min avant le dernier moment ouvert précédant {draft.date}.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <OtpButton />
        <OtpInput value={draft.otp} onChange={(v) => set({ otp: v })} />
        <Button type="button" size="sm" onClick={submit} disabled={pending || !shapeOk || draft.otp.length !== 6} data-testid="calendar-form-save">
          {pending ? "Enregistrement…" : "Enregistrer l'exception"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={pending}>
          Annuler
        </Button>
        {err && (
          <span className="text-loss" role="alert" data-testid="calendar-form-error">
            {err}
          </span>
        )}
      </div>
    </div>
  );
}

function WeeklyForm({
  weekly,
  onDone,
}: {
  weekly: NonNullable<CalendarResponse["weekly"]>;
  onDone: () => void;
}) {
  const [anchorTz, setAnchorTz] = useState(weekly.anchorTz);
  const [openDow, setOpenDow] = useState(weekly.weekOpenDow);
  const [openTime, setOpenTime] = useState(weekly.weekOpenTime);
  const [closeDow, setCloseDow] = useState(weekly.weekCloseDow);
  const [closeTime, setCloseTime] = useState(weekly.weekCloseTime);
  const [otp, setOtp] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const DOWS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

  const submit = async () => {
    setPending(true);
    setErr(null);
    try {
      await api.putCalendarWeekly({
        anchorTz,
        weekOpenDow: openDow,
        weekOpenTime: openTime,
        weekCloseDow: closeDow,
        weekCloseTime: closeTime,
        otp,
      });
      onDone();
    } catch (ex) {
      setErr(ex instanceof ApiError && ex.status === 401 ? "OTP invalide." : "Enregistrement échoué (vérifie les champs).");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="border-border bg-surface-2/50 flex flex-col gap-2 rounded-md border p-3 text-xs" data-testid="calendar-weekly-form">
      <div className="flex flex-wrap gap-3">
        <L label="Fuseau d'ancrage">
          <input type="text" className="field w-44" value={anchorTz} onChange={(e) => setAnchorTz(e.target.value)} data-testid="calendar-weekly-tz" />
        </L>
        <L label="Ouvre (jour)">
          <select className={FIELD} value={openDow} onChange={(e) => setOpenDow(e.target.value)}>
            {DOWS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </L>
        <L label="at">
          <input type="time" className={FIELD} value={openTime} onChange={(e) => setOpenTime(e.target.value)} />
        </L>
        <L label="Ferme (jour)">
          <select className={FIELD} value={closeDow} onChange={(e) => setCloseDow(e.target.value)}>
            {DOWS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </L>
        <L label="at">
          <input type="time" className={FIELD} value={closeTime} onChange={(e) => setCloseTime(e.target.value)} />
        </L>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <OtpButton />
        <OtpInput value={otp} onChange={setOtp} />
        <Button type="button" size="sm" onClick={submit} disabled={pending || otp.length !== 6} data-testid="calendar-weekly-save">
          {pending ? "Enregistrement…" : "Enregistrer l'hebdo"}
        </Button>
        {err && (
          <span className="text-loss" role="alert">
            {err}
          </span>
        )}
      </div>
    </div>
  );
}

/** « M'envoyer un OTP » — partage le purpose PARAM_APPLY du flux paramètres. */
function OtpButton() {
  const [state, setState] = useState<"idle" | "pending" | "sent" | "failed">("idle");
  const send = async () => {
    setState("pending");
    try {
      await api.requestParamOtp();
      setState("sent");
    } catch {
      setState("failed");
    }
  };
  return (
    <Button type="button" size="sm" variant="outline" onClick={send} disabled={state === "pending"} data-testid="calendar-request-otp">
      {state === "sent" ? "OTP envoyé ✓" : state === "pending" ? "Envoi…" : state === "failed" ? "Renvoyer l'OTP" : "M'envoyer un OTP"}
    </Button>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      {children}
    </label>
  );
}

function TypeBadge({ type }: { type: CalendarExceptionDay["type"] }) {
  const map = {
    CLOSED: "text-loss border-sell/45 bg-sell-soft",
    EARLY_CLOSE: "text-warning border-warning/45 bg-warning-soft",
    MODIFIED: "text-info border-info/45 bg-info-soft",
  } as const;
  const label = { CLOSED: "FERMÉ", EARLY_CLOSE: "FERM. ANTICIPÉE", MODIFIED: "MODIFIÉ" }[type];
  return (
    <span className={cn("rounded-md border px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap", map[type])}>
      {label}
    </span>
  );
}

/* ── formatting helpers ────────────────────────────────────────────────── */

function hoursOf(e: CalendarExceptionDay): string {
  if (e.type === "CLOSED") return "fermé toute la journée";
  if (e.type === "MODIFIED") return `${e.openLocal ?? "?"} → ${e.closeLocal ?? "?"}`;
  return `…${e.closeLocal ?? "?"}${e.reopenLocal ? ` → ${e.reopenLocal}` : ""}`;
}

function fmtParis(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleString("en-GB", {
      timeZone: "Europe/Paris",
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }) + " Paris"
  );
}
